import { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Package, MapPin, Search, Truck, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { PageContainer } from '@/components/common/PageContainer';
import { useApi } from '@/hooks/useApi';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface ShipmentLocation {
  id: string;
  tracking_number: string;
  from_address: {
    lat: number;
    lng: number;
    city: string;
    street: string;
    neighborhood: string;
  };
  to_address: {
    lat: number;
    lng: number;
    city: string;
    street: string;
    neighborhood: string;
  };
}

interface LocationsResponse {
  list: ShipmentLocation[];
  meta: {
    total: number;
    page: string;
    limit: string;
    totalPages: number;
  };
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'جميع الحالات' },
  { value: 'pending', label: 'قيد الانتظار' },
  { value: 'assigned', label: 'تم التعيين' },
  { value: 'picked_up', label: 'تم الاستلام' },
  { value: 'in_transit', label: 'قيد التوصيل' },
  { value: 'out_for_delivery', label: 'خارج للتسليم' },
  { value: 'delivered', label: 'تم التسليم' },
  { value: 'failed', label: 'فشل التسليم' },
  { value: 'returned', label: 'مرتجع' },
  { value: 'cancelled', label: 'ملغي' },
];

const LOCATION_TYPE_OPTIONS = [
  { value: 'pickup', label: 'موقع الاستلام' },
  { value: 'delivery', label: 'موقع التسليم' },
];

export default function MapView() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const isMobile = useIsMobile();
  
  const [locations, setLocations] = useState<ShipmentLocation[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<ShipmentLocation | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [locationType, setLocationType] = useState<'pickup' | 'delivery'>('pickup');
  
  const { get, loading } = useApi();

  const hasActiveFilters = search !== '' || status !== '' || locationType !== 'pickup';

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = L.map(mapContainer.current).setView([15.5007, 32.5599], 6); // Sudan center

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map.current);

    L.control.zoom({ position: 'topleft' }).addTo(map.current);

    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Update markers when locations or locationType changes
  useEffect(() => {
    if (!map.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add new markers
    locations.forEach(location => {
      const address = locationType === 'pickup' ? location.from_address : location.to_address;
      
      if (!address?.lat || !address?.lng) return;

      const markerColor = locationType === 'pickup' ? '#22c55e' : '#3b82f6';
      const customIcon = L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="
            background-color: ${markerColor};
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 3px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            cursor: pointer;
          ">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
          </div>
        `,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
      });

      const marker = L.marker([address.lat, address.lng], { icon: customIcon })
        .addTo(map.current!);

      const popupContent = `
        <div style="direction: rtl; text-align: right; min-width: 200px;">
          <strong style="color: ${markerColor};">${location.tracking_number}</strong><br/>
          <hr style="margin: 8px 0; border-color: #eee;"/>
          <strong>${locationType === 'pickup' ? 'موقع الاستلام' : 'موقع التسليم'}</strong><br/>
          <span>${address.city}</span><br/>
          <span>${address.neighborhood}</span><br/>
          <span>${address.street}</span>
        </div>
      `;
      marker.bindPopup(popupContent);

      marker.on('click', () => {
        setSelectedMarker(location);
      });

      markersRef.current.push(marker);
    });

    // Fit bounds if there are markers
    if (markersRef.current.length > 0) {
      const group = L.featureGroup(markersRef.current);
      map.current.fitBounds(group.getBounds(), { padding: [50, 50] });
    }
  }, [locations, locationType]);

  const handleSearch = useCallback(async () => {
    try {
      const params: Record<string, string> = {
        page: '1',
        limit: '1000',
      };

      if (search) params.search = search;
      if (status && status !== 'all') params.status = status;

      const response: any = await get('/api/v1/dashboard/shipments/locations', params);
      const data = response?.list || response?.data?.list || [];
      setLocations(data);
      setHasSearched(true);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      setLocations([]);
    }
  }, [get, search, status]);

  const handleClearFilters = () => {
    setSearch('');
    setStatus('');
    setLocationType('pickup');
    setLocations([]);
    setHasSearched(false);
    setSelectedMarker(null);
    
    // Clear markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
  };

  const FilterContent = (
    <div
      className={cn(
        'relative transition-all duration-300 ease-in-out',
        isCollapsed ? 'w-12' : 'w-52'
      )}
    >
      {isCollapsed ? (
        <div className="flex h-full flex-col items-center pt-4">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsCollapsed(false)}
          >
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <Filter className="h-5 w-5 text-muted-foreground mt-2" />
          {hasActiveFilters && (
            <div className="mt-2 h-2 w-2 rounded-full bg-primary" />
          )}
        </div>
      ) : (
        <div className="space-y-2 pe-2 h-full overflow-y-auto pb-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Filter className="h-4 w-4" />
              فلترة
            </h3>
            <div className="flex items-center gap-1">
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs text-destructive hover:text-destructive px-2"
                  onClick={handleClearFilters}
                >
                  مسح
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 hidden lg:flex"
                onClick={() => setIsCollapsed(true)}
              >
                <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute end-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="رقم التتبع"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 text-xs pe-8"
            />
          </div>

          {/* Status Filter */}
          <SearchableSelect
            options={STATUS_OPTIONS.filter(o => o.value !== 'all')}
            value={status}
            onValueChange={setStatus}
            placeholder="الحالة"
            searchPlaceholder="بحث في الحالات..."
          />

          {/* Location Type Filter */}
          <SearchableSelect
            options={LOCATION_TYPE_OPTIONS}
            value={locationType}
            onValueChange={(v) => setLocationType(v as 'pickup' | 'delivery')}
            placeholder="نوع الموقع"
            searchPlaceholder="بحث..."
          />

          {/* Search Button */}
          <Button 
            onClick={handleSearch} 
            disabled={loading} 
            className="w-full h-9 text-xs gap-2"
          >
            <Search className="h-3.5 w-3.5" />
            بحث
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-xl md:text-2xl font-bold">خريطة الشحنات</h1>
          <div className="flex gap-2">
            {/* Mobile/Tablet Filter Button */}
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Filter className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-4">
                <div className="mt-6">
                  {FilterContent}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar */}
      <div className="flex gap-4 ">
        {/* Filter Sidebar - Desktop Only */}
        <div className="hidden lg:block">
          {FilterContent}
        </div>

        {/* Map Content */}
        <div className="flex-1 min-w-0 relative">
          <Card className="overflow-hidden h-full">
            <div ref={mapContainer} className="h-full w-full rounded-lg" />
          </Card>

          {/* Stats Overlay */}
          <div className="absolute top-4 end-4 z-[1000]">
            <Card className="p-3 bg-background/95 backdrop-blur-sm">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-primary" />
                  <span className="font-medium">{locations.length}</span>
                  <span className="text-muted-foreground">شحنة</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Legend */}
          <div className="absolute bottom-4 end-4 z-[1000]">
            <Card className="p-3 bg-background/95 backdrop-blur-sm">
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-green-500" />
                  <span>موقع الاستلام</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-blue-500" />
                  <span>موقع التسليم</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Selected Marker Info */}
          {selectedMarker && (
            <div className="absolute top-4 start-4 z-[1000]">
              <Card className="p-4 bg-background/95 backdrop-blur-sm w-[280px]">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold">{selectedMarker.tracking_number}</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => setSelectedMarker(null)}
                  >
                    ×
                  </Button>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="flex items-center gap-2 text-green-600 font-medium mb-1">
                      <MapPin className="h-4 w-4" />
                      موقع الاستلام
                    </div>
                    <div className="text-muted-foreground pe-6">
                      {selectedMarker.from_address.city}, {selectedMarker.from_address.neighborhood}
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 text-blue-600 font-medium mb-1">
                      <Truck className="h-4 w-4" />
                      موقع التسليم
                    </div>
                    <div className="text-muted-foreground pe-6">
                      {selectedMarker.to_address.city}, {selectedMarker.to_address.neighborhood}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Empty State */}
          {!hasSearched && (
            <div className="absolute inset-0 z-[500] flex items-center justify-center bg-background/50 backdrop-blur-sm pointer-events-none rounded-lg">
              <Card className="p-6 text-center pointer-events-auto">
                <Search className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-semibold mb-1">ابدأ البحث</h3>
                <p className="text-sm text-muted-foreground">
                  استخدم الفلاتر ثم اضغط على زر البحث
                </p>
              </Card>
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
